import React, { useEffect, useState } from "react";
import { Link, useNavigate } from 'react-router-dom';
import { Box, Card, CardContent, Container, IconButton, MenuItem, Typography, Drawer, CircularProgress } from "@mui/material";
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import Navbar from "../../layouts/Navbar/Navbar";
import { Sidebar } from "../HomePage/partials/Sidebar/Sidebar";
import { PREBOARD_STORAGE_KEY, SETTINGS_COG_LORO_URL } from "../../utils/constants";
import { PinnacleTextField } from "../../components/PinnacleTextField";
import { ValidateTokenData } from "../../api/interfaces/Auth";
import { validate } from "../../api/services/auth";
import { handleApiError } from "../../utils/errorHandler";
import { pushNotification } from "../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider";
import { NotificationTypes } from "../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider.types";
import moment from "moment";

const SettingsPage: React.FC = () => {
    const navigate = useNavigate();
    const [mobileSidebarOpen, setMobileSidebarOpen] = useState<boolean>(false);

    const [currentUser, setCurrentUser] = useState<ValidateTokenData | null>(null);

    const [isLoading, setIsLoading] = React.useState<boolean>(false);

    useEffect(() => {
        const run = async () => {
            const token =
                localStorage.getItem(PREBOARD_STORAGE_KEY) ||
                sessionStorage.getItem(PREBOARD_STORAGE_KEY);
            if (!token) {
                navigate('/login');
                return;
            }
            try {
                setIsLoading(true);
                const validateResponse = await validate();
                setCurrentUser(validateResponse.data as unknown as ValidateTokenData);
            } catch (error: any) {
                // trigger handleApiError is status code is not 401
                if (error.response?.status !== 401) {
                    handleApiError(error, {
                        notify: (msg) => {
                            pushNotification({
                                details: msg,
                                type: NotificationTypes.Error,
                            });
                        },
                    });
                }
            } finally {
                setIsLoading(false);
            }
        };
        run();

    }, [navigate]);

    return (
        <>
            <Navbar name={`${currentUser?.firstName} ${currentUser?.lastName}`} onOpenSidebar={() => setMobileSidebarOpen(true)} />
            <main
                style={{
                    display: "flex",
                    width: "100%",
                    minHeight: "100vh",
                    backgroundColor: "#f6f6f6",
                    overflow: "hidden",
                }}
            >
                <Box
                    component="section"
                    sx={{
                        flexGrow: 1,
                        width: "100%",
                        overflowX: "hidden",
                        backgroundColor: "#EEF0FA",
                    }}
                >
                    <Container
                        maxWidth={false}
                        sx={{
                            mt: { xs: 4, sm: 6 },
                            mb: 4,
                            px: { xs: 2, sm: 3, lg: 15 },
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: { xs: "column", md: "row" },
                                gap: 3,
                            }}
                        >
                            <Box
                                sx={{
                                    flexShrink: 0,
                                    width: { xs: "100%", md: "25%" },
                                    backgroundColor: '#fff',
                                    borderRadius: '8px',
                                    overflow: "hidden",
                                }}
                            >
                                <Box
                                    sx={{
                                        flexShrink: 0,
                                        width: { xs: "100%", md: "auto" },
                                        display: { xs: "none", md: "block" },
                                    }}
                                >
                                    <Sidebar variant="desktop" user={currentUser || {}} />
                                </Box>

                                <Drawer
                                    anchor="left"
                                    open={mobileSidebarOpen}
                                    onClose={() => setMobileSidebarOpen(false)}
                                    slotProps={{
                                        paper: {
                                            sx: {
                                                width: 280,
                                                backgroundColor: "#F6F6F6",
                                                overflow: "hidden",

                                            },
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            height: "100%",
                                            overflowY: "auto",
                                            p: 2,
                                        }}
                                    >

                                        <Sidebar
                                            variant="mobile"
                                            user={currentUser || {}}
                                        />
                                    </Box>
                                </Drawer>
                            </Box>

                            <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                                <Box sx={{ width: "100%", overflow: "hidden" }}>
                                    <Card sx={{ borderRadius: '8px', '--Paper-shadow': 'none !important' }}>
                                        <CardContent sx={{ p: 3 }}>
                                            {/* Header */}
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                    mb: 3,
                                                }}
                                            >
                                                {/* Group these two items so they stay together */}
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    <IconButton
                                                        sx={{ p: 0, mr: 1 }}
                                                        onClick={() => navigate(-1)}
                                                    >
                                                        <ArrowBackOutlinedIcon sx={{
                                                            fontSize: 26,
                                                            color: "#373737"
                                                        }}
                                                        />
                                                    </IconButton>
                                                    <Typography variant="h3" fontWeight={600}>
                                                        Settings
                                                    </Typography>
                                                </Box>

                                                <Box
                                                    component="img"
                                                    src={SETTINGS_COG_LORO_URL}
                                                    alt="Settings cog logo"
                                                    sx={{
                                                        width: 40,
                                                        height: 40,
                                                        objectFit: "contain",
                                                    }}
                                                />
                                            </Box>

                                            <>
                                                <Typography variant="h3" fontWeight={600}>
                                                    Profile
                                                </Typography>


                                                {isLoading ? (
                                                    <Box
                                                        sx={{
                                                            width: "100%",
                                                            height: 300,
                                                            display: "flex",
                                                            justifyContent: "center",
                                                            alignItems: "center",
                                                        }}
                                                    >
                                                        <CircularProgress />
                                                    </Box>
                                                ) : (
                                                    <Box
                                                        sx={{
                                                            mt: 3,
                                                            display: "flex",
                                                            flexDirection: "column",
                                                            gap: 3,
                                                            maxWidth: 700,
                                                        }}
                                                    >
                                                        {/* FIRST & LAST NAME */}
                                                        <Box sx={{ display: "flex", gap: 2, flexDirection: { xs: "column", sm: "row" } }}>
                                                            <Box sx={{ flex: 1 }}>
                                                                <Typography
                                                                    sx={{
                                                                        mb: 0.5,
                                                                        fontSize: 14,
                                                                        fontWeight: 400,
                                                                        color: "#5840BA",
                                                                    }}
                                                                >
                                                                    First Name:
                                                                </Typography>
                                                                <PinnacleTextField
                                                                    fullWidth
                                                                    value={currentUser?.firstName || ""}
                                                                    disabled
                                                                />
                                                            </Box>

                                                            <Box sx={{ flex: 1 }}>
                                                                <Typography
                                                                    sx={{
                                                                        mb: 0.5,
                                                                        fontSize: 14,
                                                                        fontWeight: 400,
                                                                        color: "#5840BA",
                                                                    }}
                                                                >
                                                                    Last Name:
                                                                </Typography>
                                                                <PinnacleTextField
                                                                    fullWidth
                                                                    value={currentUser?.lastName || ""}
                                                                    disabled
                                                                />
                                                            </Box>
                                                        </Box>

                                                        {/* BIRTHDATE & EMAIL */}
                                                        <Box sx={{ display: "flex", gap: 2, flexDirection: { xs: "column", sm: "row" } }}>
                                                            <Box sx={{ flex: 1 }}>
                                                                <Typography
                                                                    sx={{
                                                                        mb: 0.5,
                                                                        fontSize: 14,
                                                                        fontWeight: 400,
                                                                        color: "#5840BA",
                                                                    }}
                                                                >
                                                                    Birthdate:
                                                                </Typography>
                                                                <PinnacleTextField
                                                                    fullWidth
                                                                    value={moment(currentUser?.birthDate).format("MM/DD/YYYY") || ""}
                                                                    disabled
                                                                />
                                                            </Box>

                                                            <Box sx={{ flex: 1 }}>
                                                                <Typography
                                                                    sx={{
                                                                        mb: 0.5,
                                                                        fontSize: 14,
                                                                        fontWeight: 400,
                                                                        color: "#5840BA",
                                                                    }}
                                                                >
                                                                    Email Address:
                                                                </Typography>
                                                                <PinnacleTextField
                                                                    fullWidth
                                                                    value={currentUser?.email || ""}
                                                                    disabled
                                                                />
                                                            </Box>
                                                        </Box>

                                                        {/* SCHOOL & SUFFIX */}
                                                        <Box sx={{ display: "flex", gap: 2, flexDirection: { xs: "column", sm: "row" } }}>
                                                            <Box sx={{ flex: 1 }}>
                                                                {currentUser?.suffix && (
                                                                    <>
                                                                        <Typography
                                                                            sx={{
                                                                                mb: 0.5,
                                                                                fontSize: 14,
                                                                                fontWeight: 400,
                                                                                color: "#5840BA",
                                                                            }}
                                                                        >
                                                                            School:
                                                                        </Typography>
                                                                        <PinnacleTextField
                                                                            fullWidth
                                                                            value={currentUser?.school || ""}
                                                                            disabled
                                                                        />
                                                                    </>
                                                                )}
                                                            </Box>

                                                            <Box sx={{ flex: 1 }}>
                                                                {currentUser?.suffix && (
                                                                    <>
                                                                        <Typography
                                                                            sx={{
                                                                                mb: 0.5,
                                                                                fontSize: 14,
                                                                                fontWeight: 400,
                                                                                color: "#5840BA",
                                                                            }}
                                                                        >
                                                                            Suffix:
                                                                        </Typography>
                                                                        <PinnacleTextField
                                                                            fullWidth
                                                                            value={currentUser?.suffix || ""}
                                                                            disabled
                                                                        />
                                                                    </>
                                                                )}
                                                            </Box>
                                                        </Box>


                                                        <Box sx={{ mt: 3 }}>
                                                            <Typography variant="h3" fontWeight={600} sx={{ pt: 3 }}>
                                                                Terms and Conditions
                                                            </Typography>

                                                            <Box
                                                                sx={{
                                                                    display: "flex",
                                                                    flexDirection: "column",
                                                                    gap: 1.5,
                                                                    mt: 3,
                                                                }}
                                                            >
                                                                <Box sx={{ display: "flex" }}>
                                                                    <Link
                                                                        to="/terms"
                                                                        style={{
                                                                            fontSize: "14px",
                                                                            fontWeight: 400,
                                                                            color: "#5840BA",
                                                                        }}
                                                                    >
                                                                        Terms and Conditions
                                                                    </Link>
                                                                </Box>

                                                                <Box sx={{ display: "flex" }}>
                                                                    <Link
                                                                        to="/privacy"
                                                                        style={{
                                                                            fontSize: "14px",
                                                                            fontWeight: 400,
                                                                            color: "#5840BA",
                                                                        }}
                                                                    >
                                                                        Privacy Policy
                                                                    </Link>
                                                                </Box>
                                                            </Box>
                                                        </Box>

                                                    </Box>
                                                )}

                                            </>
                                        </CardContent>
                                    </Card>
                                </Box>
                            </Box>
                        </Box>
                    </Container>
                </Box>
            </main>
        </>
    );
};

export default SettingsPage;
